import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { fmtEUR, fmtPct } from "@/lib/format";
import ResultsPanel from "@/components/ResultsPanel";
import { toast } from "sonner";
import { ClipboardList, ArrowRight } from "lucide-react";
import PercorsoOperativo from "@/components/PercorsoOperativo";
import BeniBreakdown from "@/components/BeniBreakdown";
import PuntiForza from "@/components/PuntiForza";
import { REGIONI_IRAP } from "@/lib/irap";
import { useAuth } from "@/lib/auth";

const DEFAULTS = {
  investimento: 100000,
  has_materiali: true,
  importo_materiali: 100000,
  has_fotovoltaico: false,
  importo_fotovoltaico: 0,
  aliquota_ammortamento: 0.125,
  aliquota_ires: 0.24,
  aliquota_irap: 0.039,
  regione: "lombardia",
  anno_inizio: 2026,
  tipologia: "industria_4_0",
  sabatini: "nessuna",
  sabatini_parziale: false,
  sabatini_base: 0,
};

export default function Calculator() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [showBeni, setShowBeni] = useState(false);
  const [currentDossierId, setCurrentDossierId] = useState(null);
  const [beniRows, setBeniRows] = useState([]);

  // Tutte le funzionalità del calcolatore (PDF, Excel, calcolo avanzato) sono ora pubbliche.
  const featuresUnlocked = true;

  // Investimento totale = somma materiali + fotovoltaico. Sabatini si applica solo alla parte materiali.
  const totInvestimento =
    (form.has_materiali ? Number(form.importo_materiali) || 0 : 0) +
    (form.has_fotovoltaico ? Number(form.importo_fotovoltaico) || 0 : 0);

  // Sync stato derivato ogni volta che cambiano le fasi
  useEffect(() => {
    setForm((f) => {
      const tot =
        (f.has_materiali ? Number(f.importo_materiali) || 0 : 0) +
        (f.has_fotovoltaico ? Number(f.importo_fotovoltaico) || 0 : 0);
      const materialiVal = f.has_materiali ? Number(f.importo_materiali) || 0 : 0;
      const isSab = f.sabatini !== "nessuna";
      // Sabatini: base sempre editabile, clampata al max = importo_materiali.
      // Se non attiva → base = 0. Se attivata da poco (base=0 mentre isSab) → default a materialiVal.
      let nextBase = Number(f.sabatini_base) || 0;
      if (!isSab) {
        nextBase = 0;
      } else if (nextBase <= 0) {
        nextBase = materialiVal;
      } else if (nextBase > materialiVal) {
        nextBase = materialiVal;
      }
      // sabatini_parziale è vero quando la base è minore dell'importo materiali (o c'è fotovoltaico)
      const nextParziale = isSab && (nextBase < materialiVal || f.has_fotovoltaico);
      return {
        ...f,
        investimento: tot,
        sabatini_parziale: nextParziale,
        sabatini_base: nextBase,
      };
    });
  }, [form.has_materiali, form.importo_materiali, form.has_fotovoltaico, form.importo_fotovoltaico, form.sabatini]);

  // Aliquota media ponderata dai beni (colonna 3), applicata quando la ripartizione è attiva e valida
  const beniTotale = beniRows.reduce((s, r) => s + (parseFloat(r.importo) || 0), 0);
  const beniOverflow = beniTotale > Number(form.investimento) + 0.5;
  const beniAliquota = beniTotale > 0
    ? beniRows.reduce((s, r) => s + (parseFloat(r.importo) || 0) * (parseFloat(r.aliquota) || 0), 0) / beniTotale / 100
    : null;
  const usaBreakdown = featuresUnlocked && showBeni && beniTotale > 0 && !beniOverflow;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = useCallback(async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    const silent = opts.silent === true;
    if (beniOverflow) {
      if (!silent) toast.error("La somma dei beni supera l'investimento totale. Correggi prima di calcolare.");
      return;
    }
    if (totInvestimento <= 0) {
      if (!silent) toast.error("Attiva almeno una fase (beni materiali o fotovoltaico) e inserisci un importo.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        investimento: totInvestimento,
        aliquota_ammortamento: Number(form.aliquota_ammortamento),
        aliquota_ires: Number(form.aliquota_ires),
        aliquota_irap: Number(form.aliquota_irap),
        anno_inizio: Number(form.anno_inizio),
        tipologia: form.tipologia,
        sabatini: form.sabatini,
        sabatini_base:
          form.sabatini !== "nessuna"
            ? Number(form.sabatini_base) || 0
            : null,
      };
      if (usaBreakdown) {
        payload.beni = beniRows
          .filter((r) => parseFloat(r.importo) > 0 && parseFloat(r.aliquota) > 0)
          .map((r) => ({
            importo: parseFloat(r.importo),
            aliquota: parseFloat(r.aliquota) / 100,
            label: r.label || null,
          }));
      }
      const { data } = await api.post("/calc/simulate", payload);
      setResult(data);
    } catch (err) {
      if (!silent) toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [beniOverflow, totInvestimento, form, usaBreakdown, beniRows]);

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  // Auto-calcolo iniziale (solo al mount): usa il valore corrente di `submit` senza legarlo alle deps
  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) return;
    didMountRef.current = true;
    submit(null, { silent: true });
  }, [submit]);

  // Caricamento dossier esistente da query param ?dossier=<id>
  const loadDossierId = searchParams.get("dossier");
  useEffect(() => {
    if (!loadDossierId || !user || user.role !== "user") return;
    (async () => {
      try {
        const { data } = await api.get(`/dossier/${loadDossierId}`);
        const snap = data?.calc_snapshot;
        if (!snap) {
          toast.info("Nessun calcolo salvato per questa pratica");
          return;
        }
        // Ripristina lo stato del calcolatore
        if (snap._form) {
          setForm((prev) => ({ ...prev, ...snap._form }));
        }
        if (Array.isArray(snap._beni_rows) && snap._beni_rows.length > 0) {
          setBeniRows(snap._beni_rows);
        }
        // Al riapri, la pratica mostra sempre il calcolo avanzato
        setShowBeni(true);
        setResult(snap);
        setCurrentDossierId(loadDossierId);
        setSaveName(data.note || data.cliente?.note || `Pratica ${data.id.slice(0, 8)}`);
        toast.success("Calcolo caricato dal dossier");
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Impossibile caricare il dossier");
      }
    })();
  }, [loadDossierId, user]);

  // Auto-ricalcolo debounced: 500ms dopo l'ultimo cambio di un parametro
  useEffect(() => {
    if (totInvestimento <= 0) return;
    if (beniOverflow) return;
    const t = setTimeout(() => {
      submit(null, { silent: true });
    }, 500);
    return () => clearTimeout(t);
  }, [submit, totInvestimento, beniOverflow]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold text-ink tracking-tight" data-testid="page-title">
            Calcolatore
          </h1>
          <p className="mt-2 text-ink-muted">Iperammortamento 2026 · L. 199/2025</p>
        </div>
      </div>

      {/* ===================== 3 FASI (in cima, centrate) ===================== */}
      <div className="max-w-3xl mx-auto mb-10">
        <PuntiForza variant="compact" className="mb-6" />
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand/10 text-brand rounded-full text-[11px] font-display uppercase tracking-[0.2em]">
            Configura in 3 fasi
          </div>
          <p className="mt-2 text-sm text-ink-muted">Attiva le fasi che compongono il tuo investimento e inserisci gli importi.</p>
        </div>

        <div className="space-y-3">
          {/* FASE 1 */}
          <PhaseBlock
            phase="1"
            label="Investimento in immobilizzazioni materiali e immateriali"
            checked={form.has_materiali}
            onToggle={(v) => update("has_materiali", v)}
            testid="phase-materiali"
            sublabel="Macchinari, impianti, attrezzature, software, licenze — base per Iperammortamento e Sabatini."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <NumberField
                label="Importo (€)"
                value={form.importo_materiali}
                onChange={(v) => update("importo_materiali", v)}
                testid="input-importo-materiali"
                step="1000"
              />
              <SelectField
                label="Regione (aliquota IRAP)"
                value={form.regione}
                onChange={(v) => {
                  const r = REGIONI_IRAP.find((x) => x.key === v);
                  if (r && r.aliquota !== null) {
                    setForm((prev) => ({ ...prev, regione: v, aliquota_irap: r.aliquota / 100 }));
                  } else {
                    setForm((prev) => ({ ...prev, regione: v }));
                  }
                }}
                testid="input-regione"
                options={REGIONI_IRAP.map((r) => ({ value: r.key, label: r.label }))}
              />
            </div>
            <div className="mt-2 text-[11px] font-mono text-ink-muted">
              Aliquota IRAP applicata: <span className="text-brand font-semibold">{fmtPct(form.aliquota_irap)}</span>
            </div>
          </PhaseBlock>

          {/* FASE 2 */}
          <PhaseBlock
            phase="2"
            label="Investimento in fotovoltaico"
            checked={form.has_fotovoltaico}
            onToggle={(v) => update("has_fotovoltaico", v)}
            testid="phase-fotovoltaico"
            sublabel="Impianti di autoproduzione da fonti rinnovabili per autoconsumo (art. 1 c. 428 L. 199/2025). Non ammesso alla Nuova Sabatini."
          >
            <NumberField
              label="Importo (€)"
              value={form.importo_fotovoltaico}
              onChange={(v) => update("importo_fotovoltaico", v)}
              testid="input-importo-fotovoltaico"
              step="1000"
            />
          </PhaseBlock>

          {/* FASE 3 */}
          <PhaseBlock
            phase="3"
            label="Investimento finanziato con Nuova Sabatini"
            checked={form.sabatini !== "nessuna"}
            onToggle={(v) => update("sabatini", v ? "ordinaria" : "nessuna")}
            testid="phase-sabatini"
            sublabel="Aliquota 7,72% · min 20.000€ · max 4.000.000€."
            disabled={!form.has_materiali}
            disabledHint="Attiva la Fase 1 (beni materiali/immateriali) per abilitare la Sabatini."
          >
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-2.5 leading-snug">
              <strong>Attenzione:</strong> la Nuova Sabatini può essere richiesta <u>solo sugli investimenti in beni
              materiali e immateriali</u>. L&apos;importo destinato a fotovoltaico è escluso dalla base di calcolo Sabatini.
            </div>
            <NumberField
              label={`Importo per il quale richiedi l'agevolazione (max ${fmtEUR(Number(form.importo_materiali) || 0)})`}
              value={form.sabatini_base}
              onChange={(v) => {
                const max = Number(form.importo_materiali) || 0;
                const val = Math.max(0, Math.min(Number(v) || 0, max));
                update("sabatini_base", val);
              }}
              testid="input-sabatini-base"
              step="1000"
            />
            {Number(form.sabatini_base) > 0 && Number(form.importo_materiali) > 0 && (
              <div className="text-[11px] text-ink-muted">
                Utilizzi <strong className="text-brand font-mono">{fmtEUR(form.sabatini_base)}</strong> su{" "}
                <span className="font-mono">{fmtEUR(form.importo_materiali)}</span> di base materiali/immateriali
                ({((Number(form.sabatini_base) / Number(form.importo_materiali)) * 100).toFixed(0)}%).
              </div>
            )}
          </PhaseBlock>
        </div>

        {/* Riepilogo totale */}
        <div className="mt-4 bg-ink text-white rounded-lg p-4 flex items-center justify-between">
          <span className="text-[11px] font-mono uppercase tracking-widest text-white/60">Investimento totale</span>
          <span className="font-mono text-2xl font-semibold" data-testid="tot-investimento">{fmtEUR(totInvestimento)}</span>
        </div>

        {/* Impostazioni fiscali avanzate (accordion) */}
        <details className="mt-4 bg-white border border-line rounded-lg overflow-hidden group" data-testid="impostazioni-avanzate">
          <summary className="px-4 py-3 cursor-pointer flex items-center justify-between hover:bg-line/20 text-sm text-ink font-display uppercase tracking-widest text-xs">
            <span>Impostazioni fiscali avanzate</span>
            <span className="text-brand text-[10px]">personalizza aliquote →</span>
          </summary>
          <div className="p-4 border-t border-line grid grid-cols-1 sm:grid-cols-3 gap-4">
            <PctField
              label="Aliquota ammortamento fiscale"
              value={form.aliquota_ammortamento}
              onChange={(v) => update("aliquota_ammortamento", v)}
              testid="input-aliquota-amm"
            />
            <PctField
              label="Aliquota IRES / IRPEF"
              value={form.aliquota_ires}
              onChange={(v) => update("aliquota_ires", v)}
              testid="input-aliquota-ires"
            />
            <NumberField
              label="Anno inizio ammortamento"
              value={form.anno_inizio}
              onChange={(v) => update("anno_inizio", v)}
              testid="input-anno"
              step="1"
            />
          </div>
        </details>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Results only — la colonna sinistra form è stata rimossa (workflow linearization).
            Le impostazioni fiscali avanzate ora vivono in un accordion sopra il grid.
            Il calcolo si aggiorna automaticamente ad ogni modifica. */}
        <form onSubmit={submit} className="hidden" data-testid="calc-form" />

        {/* Results column */}
        <div className="space-y-6">
          {result ? (
            <>
              <ResultsPanel
                result={result}
                isLogged={true}
                onRequireAuth={() => {}}
                actionsSlot={
                  !showBeni ? (
                    <button
                      type="button"
                      onClick={() => setShowBeni(true)}
                      data-testid="cta-open-beni"
                      className="group w-full relative overflow-hidden rounded-lg bg-bg-dark hover:bg-ink text-white border border-brand hover:shadow-brand-glow p-6 text-left transition"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand text-white flex items-center justify-center">
                          <ClipboardList className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="inline-flex items-center gap-2 px-2 py-0.5 bg-brand text-white rounded-full text-[10px] font-display uppercase tracking-[0.2em] mb-1.5">
                            Prossimo passo
                          </div>
                          <h3 className="font-display text-xl text-white">
                            Attiva il calcolo avanzato per singolo bene
                          </h3>
                          <p className="mt-1 text-xs text-white/60 leading-snug">
                            Ripartisci l'investimento su più beni con aliquote specifiche per ricalcolare
                            il piano di ammortamento e il grafico con la reale durata di ogni asset.
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-brand flex-shrink-0 group-hover:translate-x-0.5 transition" />
                      </div>
                    </button>
                  ) : null
                }
                beniSlot={
                  showBeni ? (
                    <div className="bg-white border border-line rounded-lg shadow-card p-6" data-testid="beni-toggle-wrapper">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Calcolo avanzato</div>
                          <h3 className="font-display text-lg text-ink">Dettaglio investimento per singolo bene</h3>
                          <p className="mt-1 text-xs text-ink-muted max-w-2xl">
                            Suddividi l&apos;investimento per bene con aliquote specifiche di ogni categoria.
                            Ricalcola il piano di ammortamento e il grafico con la reale durata di ogni asset.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowBeni(false)}
                          data-testid="toggle-beni-close"
                          className="inline-flex items-center gap-2 h-9 px-3 border border-line rounded-md text-xs text-ink hover:bg-line/40"
                        >
                          Chiudi
                        </button>
                      </div>

                      <div className="mt-4">
                        <BeniBreakdown
                          rows={beniRows}
                          setRows={setBeniRows}
                          investimentoTotale={form.investimento}
                        />
                        {usaBreakdown && (
                          <p className="mt-3 text-[11px] text-brand font-mono">
                            Breakdown attivo: piano e grafico usano le aliquote per bene.
                          </p>
                        )}
                      </div>
                    </div>
                  ) : null
                }
              />
            </>
          ) : (
            <div className="bg-white border border-line rounded-lg p-10 text-center text-ink-muted">
              Inserisci i parametri per avviare la simulazione.
            </div>
          )}
        </div>
      </div>

      <PercorsoOperativo />
    </div>
  );
}

function PhaseBlock({ phase, label, sublabel, checked, onToggle, testid, children, disabled = false, disabledHint }) {
  const active = checked && !disabled;
  return (
    <div
      className={`relative border rounded-md transition ${
        disabled
          ? "border-line/60 bg-line/20 opacity-70"
          : active
          ? "border-brand bg-brand/[0.04] shadow-[0_0_0_1px_rgba(26,171,103,0.15)]"
          : "border-line bg-white hover:border-brand/40"
      }`}
      data-testid={`${testid}-block`}
    >
      <label className={`flex items-start gap-3 p-3 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
        <div
          className={`flex-shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center font-mono text-[11px] font-semibold ${
            active ? "bg-brand text-white" : "bg-line text-ink-muted"
          }`}
        >
          {phase}
        </div>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onToggle(e.target.checked)}
          className="mt-1 w-4 h-4 accent-brand disabled:opacity-40"
          data-testid={testid}
        />
        <span className="flex-1 text-[12px] text-ink leading-snug">
          <span className="font-semibold block">{label}</span>
          {sublabel && <span className="block text-[11px] text-ink-muted mt-0.5">{sublabel}</span>}
          {disabled && disabledHint && (
            <span className="block text-[11px] text-amber-800 mt-1 italic">{disabledHint}</span>
          )}
        </span>
      </label>
      {active && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-brand/20" data-testid={`${testid}-content`}>
          {children}
        </div>
      )}
    </div>
  );
}

function FeatureTile({ title, desc }) {
  return (
    <div className="bg-white border border-line rounded-md p-4">
      <div className="w-2 h-2 rounded-full bg-brand mb-2" />
      <div className="font-display uppercase text-[11px] tracking-widest text-ink">{title}</div>
      <p className="mt-1.5 text-xs text-ink-muted leading-relaxed">{desc}</p>
    </div>
  );
}

function TextField({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-10 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
      />
    </label>
  );
}

function NumberField({ label, value, onChange, testid, step }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">{label}</span>
      <input
        type="number"
        step={step || "any"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-10 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand font-mono text-sm text-right"
      />
    </label>
  );
}

function PctField({ label, value, onChange, testid }) {
  const [text, setText] = useState((Number(value) * 100).toString());
  useEffect(() => {
    setText((Number(value) * 100).toString());
  }, [value]);
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">{label} (%)</span>
      <div className="relative">
        <input
          type="number"
          step="0.1"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            const n = parseFloat(e.target.value);
            if (!isNaN(n)) onChange(n / 100);
          }}
          data-testid={testid}
          className="w-full h-10 px-3 pr-8 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand font-mono text-sm text-right"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted text-sm font-mono">%</span>
      </div>
    </label>
  );
}

function SelectField({ label, value, onChange, testid, options }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-10 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function ScaglioniPreview({ tip, config }) {
  if (!tip || !config) return null;
  const s = config.scaglioni;
  const rows = [
    ["0 - 2,5M€", tip.s1],
    ["2,5M - 10M€", tip.s2],
    ["10M - 20M€", tip.s3],
  ];
  return (
    <div className="space-y-1">
      {rows.map(([r, c]) => (
        <div key={r} className="flex items-center justify-between text-[11px] font-mono">
          <span className="text-ink-muted">{r}</span>
          <span className="text-ink font-semibold">+{(c * 100).toFixed(0)}%</span>
        </div>
      ))}
    </div>
  );
}
